import { collection, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../services/firebase";

const PENDING_PREFERENCES_KEY = "manucalc_pending_user_preferences_sync_v1";
export const USER_PREFERENCES_SYNC_EVENT = "manucalc:user-preferences-updated";

const safePreferenceId = (key) =>
  String(key || "preference")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .slice(0, 150);

const readPendingPreferences = () => {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(window.localStorage.getItem(PENDING_PREFERENCES_KEY) || "[]");
  } catch {
    return [];
  }
};

export const getPendingUserPreferenceSyncCount = () => readPendingPreferences().length;

const writePendingPreferences = (items) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PENDING_PREFERENCES_KEY, JSON.stringify(items.slice(-100)));
};

const persistLocalPreference = (key, value) => {
  if (typeof window === "undefined" || !key) return;
  window.localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
};

const dispatchPreferenceEvents = (key) => {
  if (typeof window === "undefined") return;
  if (String(key).startsWith("manucalc_3s_workspace_")) {
    window.dispatchEvent(
      new CustomEvent("manucalc:workspace-preferences-updated", {
        detail: { uid: String(key).replace("manucalc_3s_workspace_", "") || "guest" },
      })
    );
  }
};

export const syncLocalPreference = async (key, value, user = auth.currentUser) => {
  if (!key) return false;
  persistLocalPreference(key, value);
  if (!user?.uid || !db) {
    writePendingPreferences([
      ...readPendingPreferences().filter((item) => item.key !== key),
      { key, value, updatedAt: new Date().toISOString() },
    ]);
    return false;
  }

  const preferenceId = safePreferenceId(key);
  await setDoc(
    doc(db, "users", user.uid, "userPreferences", preferenceId),
    {
      id: preferenceId,
      key,
      value,
      ownerId: user.uid,
      updatedAt: new Date().toISOString(),
      updatedAtServer: serverTimestamp(),
    },
    { merge: true }
  );
  return true;
};

const flushPendingPreferences = async (user) => {
  const pending = readPendingPreferences();
  if (!pending.length || !user?.uid) return;
  writePendingPreferences([]);
  await Promise.all(pending.map((item) => syncLocalPreference(item.key, item.value, user)));
};

let unsubscribePreferences = null;

export const stopUserPreferenceCloudSync = () => {
  if (unsubscribePreferences) {
    unsubscribePreferences();
    unsubscribePreferences = null;
  }
};

export const startUserPreferenceCloudSync = (user) => {
  stopUserPreferenceCloudSync();
  if (!user?.uid || !db) return () => {};

  flushPendingPreferences(user).catch((error) => {
    console.warn("Unable to flush pending ManuCalc preferences", error);
  });

  unsubscribePreferences = onSnapshot(
    collection(db, "users", user.uid, "userPreferences"),
    (snapshot) => {
      snapshot.docs.forEach((preferenceDoc) => {
        const preference = preferenceDoc.data();
        if (preference?.key) {
          persistLocalPreference(preference.key, preference.value);
          dispatchPreferenceEvents(preference.key);
        }
      });
      window.dispatchEvent(new CustomEvent(USER_PREFERENCES_SYNC_EVENT));
    },
    (error) => {
      console.warn("ManuCalc preference sync failed", error);
    }
  );

  return stopUserPreferenceCloudSync;
};
